import React, { useState, useRef, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import uploadService from '../../../services/uploadService';
import { useAuth } from '../../../contexts/AuthContext';

const FileUploadZone = ({ onFileUpload, isUploading }) => {
  const { user, loading, authError } = useAuth();
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [error, setError] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileStatuses, setFileStatuses] = useState({});
  const [currentFileIndex, setCurrentFileIndex] = useState(-1);
  const [overallStatus, setOverallStatus] = useState('');
  const [isDryRun, setIsDryRun] = useState(false);
  const [preflightResults, setPreflightResults] = useState({});
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === 'dragenter' || e?.type === 'dragover') {
      setDragActive(true);
    } else if (e?.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    
    const files = Array.from(e?.dataTransfer?.files || []);
    if (files?.length > 0) {
      handleMultipleFiles(files);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e?.target?.files || []);
    if (files?.length > 0) {
      handleMultipleFiles(files);
    }
  };

  // Enhanced handleMultipleFiles with preflight validation
  const handleMultipleFiles = useCallback(async (files) => {
    setError('');
    setSelectedFiles(files);
    setUploadStatus(`${files?.length} file(s) selected - Running preflight checks...`);
    
    // Initialize file statuses
    const initialStatuses = {};
    files?.forEach((file, index) => {
      initialStatuses[index] = {
        name: file?.name,
        status: 'checking',
        progress: 0,
        message: 'Running preflight validation...'
      };
    });
    setFileStatuses(initialStatuses);
    
    // Enhanced authentication check before processing
    if (!user && !loading) {
      setError('Authentication required: Please sign in using the header menu to upload files. Once signed in, you can upload multiple files at once.');
      return;
    }

    if (loading) {
      setUploadStatus('Checking authentication...');
      return;
    }

    // Show authentication error if present
    if (authError) {
      setError(`Authentication issue: ${authError}. Please try signing in again.`);
      return;
    }

    // Run preflight validation for each file
    const preflightData = {};
    let hasErrors = false;
    
    for (let i = 0; i < files?.length; i++) {
      const file = files?.[i];
      
      try {
        updateFileStatus(i, 'checking', 25, 'Creating upload batch...');
        
        // Create temporary batch for preflight
        const fileHash = await uploadService?.generateFileHash(file);
        const batchResult = await uploadService?.createBatch(file?.name, file?.size, fileHash);
        
        if (!batchResult?.success) {
          throw new Error(batchResult?.error || 'Failed to create upload batch');
        }
        
        updateFileStatus(i, 'checking', 50, 'Analyzing file structure...');
        
        // Run preflight validation
        const preflightResult = await uploadService?.validateFilePreflight(file, batchResult?.data?.id);
        preflightData[i] = preflightResult;
        
        if (preflightResult?.success) {
          if (preflightResult?.status === 'failed') {
            updateFileStatus(i, 'error', 0, `❌ Preflight failed: ${preflightResult?.errors?.[0]?.message}`);
            hasErrors = true;
          } else if (preflightResult?.status === 'warning') {
            updateFileStatus(i, 'warning', 100, `⚠️ Warnings: ${preflightResult?.errors?.[0]?.message}`);
          } else {
            updateFileStatus(i, 'ready', 100, `✓ Preflight passed: ${preflightResult?.columnCount} columns detected`);
          }
        } else {
          updateFileStatus(i, 'error', 0, `❌ Preflight error: ${preflightResult?.error}`);
          hasErrors = true;
        }
        
      } catch (error) {
        updateFileStatus(i, 'error', 0, `❌ Validation failed: ${error?.message}`);
        hasErrors = true;
      }
    }
    
    setPreflightResults(preflightData);
    
    if (hasErrors) {
      setUploadStatus(`${files?.length} file(s) checked - Some files have validation errors. Fix issues before uploading.`);
      setError('Some files failed preflight validation. Please review the errors and fix the issues before uploading.');
    } else {
      setUploadStatus(`${files?.length} file(s) ready to upload. All preflight checks passed.`);
    }
  }, [user, loading, authError]);

  // Update file status
  const updateFileStatus = (index, status, progress = 0, message = '') => {
    setFileStatuses(prev => ({
      ...prev,
      [index]: {
        ...prev?.[index],
        status,
        progress,
        message
      }
    }));
  };

  // Enhanced start upload with better progress tracking
  const startUpload = async () => {
    if (selectedFiles?.length === 0) return;
    
    // Final authentication check before upload
    if (!user) {
      setError('Authentication expired: Please sign in again before uploading files.');
      return;
    }

    setUploadProgress(0);
    setError('');
    setOverallStatus('Starting upload...');
    
    try {
      const totalFiles = selectedFiles?.length;
      let completedFiles = 0;
      const results = [];

      // Initialize all files as processing
      selectedFiles?.forEach((_, index) => {
        updateFileStatus(index, 'waiting', 0, 'Waiting to start...');
      });

      for (let i = 0; i < selectedFiles?.length; i++) {
        const file = selectedFiles?.[i];
        setCurrentFileIndex(i);
        setOverallStatus(`Processing file ${i + 1} of ${totalFiles}: ${file?.name}`);
        
        // Update current file status
        updateFileStatus(i, 'processing', 0, 'Starting upload...');
        
        const result = await processIndividualFile(file, i, totalFiles);
        results?.push(result);
        
        completedFiles++;
        const overallProgress = Math.round((completedFiles / totalFiles) * 100);
        setUploadProgress(overallProgress);
        
        // Update file status based on result
        if (result?.success) {
          updateFileStatus(i, 'completed', 100, `✓ Processed: ${result?.processed} records`);
        } else {
          updateFileStatus(i, 'error', 0, `✗ Failed: ${result?.error}`);
        }
      }

      // Summary of all uploads
      const successful = results?.filter(r => r?.success)?.length;
      const failed = results?.filter(r => !r?.success)?.length;
      
      setOverallStatus(`Upload completed! ${successful} successful, ${failed} failed.`);
      setCurrentFileIndex(-1);
      
      if (successful > 0) {
        onFileUpload?.({
          success: true,
          totalFiles,
          successful,
          failed,
          results
        });
      }

    } catch (error) {
      setError(`Upload failed: ${error?.message}`);
      setOverallStatus('');
      setCurrentFileIndex(-1);
    }

    // Reset after 8 seconds to allow user to see final results
    setTimeout(() => {
      setUploadProgress(0);
      setOverallStatus('');
      setError('');
      setSelectedFiles([]);
      setFileStatuses({});
      setCurrentFileIndex(-1);
      setPreflightResults({});
      if (fileInputRef?.current) {
        fileInputRef.current.value = '';
      }
    }, 8000);
  };

  // Enhanced processIndividualFile with dry run support
  const processIndividualFile = async (file, fileIndex, totalFiles) => {
    try {
      // Update progress: Generating hash
      updateFileStatus(fileIndex, 'processing', 10, 'Generating file hash...');
      
      const fileHash = await uploadService?.generateFileHash(file);
      
      // Update progress: Creating batch
      updateFileStatus(fileIndex, 'processing', 20, 'Creating upload batch...');
      
      const batchResult = await uploadService?.createBatch(
        file?.name,
        file?.size,
        fileHash
      );

      if (!batchResult?.success) {
        // Enhanced error messages for common authentication issues
        let errorMessage = batchResult?.error || 'Failed to create upload batch';
        
        if (errorMessage?.includes('User must be authenticated')) {
          errorMessage = 'Session expired: Please sign out and sign back in using the header menu, then try uploading again.';
        } else if (errorMessage?.includes('Profile verification failed')) {
          errorMessage = 'Account verification failed: Please contact your administrator to verify your account is properly set up.';
        } else if (errorMessage?.includes('Database permission denied')) {
          errorMessage = 'Database access denied: Please sign out and sign back in to refresh your session, then try again.';
        } else if (errorMessage?.includes('session is about to expire')) {
          errorMessage = 'Session expired: Please sign out and sign back in to refresh your session.';
        }
        
        throw new Error(errorMessage);
      }

      // Update progress: Reading file
      updateFileStatus(fileIndex, 'processing', 40, 'Reading file content...');

      // Read file content based on type
      let records = [];
      const fileExtension = file?.name?.toLowerCase()?.split('.')?.pop();
      
      if (fileExtension === 'csv') {
        const text = await file?.text();
        
        // Update progress: Parsing CSV
        updateFileStatus(fileIndex, 'processing', 60, 'Parsing CSV data...');
        
        const parseResult = uploadService?.parseCSV(text);
        
        if (!parseResult?.success) {
          throw new Error(parseResult?.error || 'Failed to parse CSV file');
        }
        
        records = parseResult?.records || [];
      } else if (['xlsx', 'xls']?.includes(fileExtension)) {
        // Update progress: Parsing Excel
        updateFileStatus(fileIndex, 'processing', 60, 'Parsing Excel data...');
        
        const parseResult = await uploadService?.parseExcel(file);
        
        if (!parseResult?.success) {
          throw new Error(parseResult?.error || 'Failed to parse Excel file');
        }
        
        records = parseResult?.records || [];
      }

      if (records?.length === 0) {
        throw new Error('No valid records found in the file');
      }

      // Update progress: Processing records
      const modeText = isDryRun ? 'Dry run processing' : 'Processing';
      updateFileStatus(fileIndex, 'processing', 80, `${modeText} ${records?.length} records...`);

      // Process the batch data with dry run support
      const processResult = await uploadService?.processBatchData(
        batchResult?.data?.id,
        records,
        isDryRun
      );

      if (processResult?.success) {
        const resultText = isDryRun 
          ? `✓ Dry run complete: Would insert ${processResult?.inserted}, skip ${processResult?.duplicates}, ${processResult?.errors} errors`
          : `✓ Processed: ${processResult?.inserted} inserted, ${processResult?.duplicates} duplicates, ${processResult?.errors} errors`;
          
        return {
          success: true,
          filename: file?.name,
          processed: processResult?.processed,
          inserted: processResult?.inserted,
          duplicates: processResult?.duplicates,
          errors: processResult?.errors,
          isDryRun,
          summary: processResult?.summary
        };
      } else {
        throw new Error(processResult?.error || 'Failed to process records');
      }

    } catch (error) {
      console.error('File upload error:', {
        message: error?.message,
        filename: file?.name,
        user: user?.id,
        timestamp: new Date()?.toISOString()
      });
      
      return {
        success: false,
        filename: file?.name,
        error: error?.message,
        isDryRun
      };
    }
  };

  const openFileDialog = () => {
    fileInputRef?.current?.click();
  };

  const removeFile = (indexToRemove) => {
    const updatedFiles = selectedFiles?.filter((_, index) => index !== indexToRemove);
    setSelectedFiles(updatedFiles);
    
    // Update file statuses
    const updatedStatuses = {};
    updatedFiles?.forEach((file, newIndex) => {
      const oldIndex = selectedFiles?.findIndex(f => f === file);
      if (oldIndex !== -1 && fileStatuses?.[oldIndex]) {
        updatedStatuses[newIndex] = {
          ...fileStatuses?.[oldIndex],
          name: file?.name
        };
      }
    });
    setFileStatuses(updatedStatuses);
    
    if (updatedFiles?.length === 0) {
      setUploadStatus('');
    } else {
      setUploadStatus(`${updatedFiles?.length} file(s) ready to upload`);
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center space-x-3 mb-4">
        <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
          <Icon name="Upload" size={20} color="white" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Enhanced File Upload with Diagnostics</h3>
          <p className="text-sm text-muted-foreground">Upload CSV/Excel files with comprehensive validation and error reporting</p>
          {/* Enhanced authentication status indicator */}
          {loading ? (
            <div className="flex items-center space-x-2 mt-1">
              <div className="w-3 h-3 border-2 border-muted-foreground border-t-primary rounded-full animate-spin"></div>
              <p className="text-xs text-muted-foreground">Checking authentication...</p>
            </div>
          ) : user ? (
            <p className="text-xs text-accent mt-1">✓ Authenticated as {user?.email}</p>
          ) : (
            <p className="text-xs text-red-600 mt-1">⚠ Not authenticated - Please sign in using the header menu</p>
          )}
        </div>
      </div>
      {/* Dry Run Toggle */}
      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Icon name="TestTube" size={16} className="text-blue-600" />
            <div>
              <p className="text-sm font-medium text-blue-800">Dry Run Mode</p>
              <p className="text-xs text-blue-700">Preview upload results without actually inserting records</p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={isDryRun}
              onChange={(e) => setIsDryRun(e?.target?.checked)}
              disabled={uploadProgress > 0}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div>
      {/* Enhanced authentication warning with clearer instructions */}
      {!loading && !user && (
        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <Icon name="AlertTriangle" size={20} className="text-yellow-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-yellow-800 mb-1">Authentication Required</p>
              <p className="text-sm text-yellow-700 mb-2">You must be signed in to upload files. Please use the "Sign In" button in the header menu to authenticate your account.</p>
              <div className="text-xs text-yellow-600">
                <p>• Click "Sign In" in the top-right corner of the page</p>
                <p>• Enter your admin credentials</p>
                <p>• Return to this page to upload files</p>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Show auth error if present */}
      {authError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start space-x-2">
            <Icon name="AlertCircle" size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-red-800">Authentication Error</p>
              <p className="text-sm text-red-700">{authError}</p>
              <p className="text-xs text-red-600 mt-1">Please sign out and sign back in to resolve this issue.</p>
            </div>
          </div>
        </div>
      )}
      {/* Enhanced Selected Files List with Preflight Status */}
      {selectedFiles?.length > 0 && (
        <div className="mb-4 p-3 bg-muted/30 rounded-lg">
          <h4 className="text-sm font-medium text-foreground mb-2">
            Selected Files with Preflight Results:
          </h4>
          <div className="space-y-3">
            {selectedFiles?.map((file, index) => {
              const status = fileStatuses?.[index];
              const preflight = preflightResults?.[index];
              const isCurrentFile = currentFileIndex === index;
              
              return (
                <div key={index} className={`bg-card p-3 rounded border transition-all duration-300 ${
                  isCurrentFile ? 'ring-2 ring-primary border-primary bg-primary/5' : ''
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      {/* Enhanced Status Icon */}
                      <div className="flex items-center justify-center w-6 h-6">
                        {status?.status === 'processing' && (
                          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                        )}
                        {status?.status === 'completed' && (
                          <Icon name="CheckCircle" size={16} className="text-green-500" />
                        )}
                        {status?.status === 'error' && (
                          <Icon name="XCircle" size={16} className="text-red-500" />
                        )}
                        {status?.status === 'warning' && (
                          <Icon name="AlertTriangle" size={16} className="text-yellow-500" />
                        )}
                        {status?.status === 'checking' && (
                          <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                        )}
                        {(status?.status === 'ready' || !status?.status) && (
                          <Icon name="FileText" size={16} className="text-green-500" />
                        )}
                      </div>
                      <span className="text-sm text-foreground font-medium">{file?.name}</span>
                      <span className="text-xs text-muted-foreground">
                        ({(file?.size / 1024 / 1024)?.toFixed(2)} MB)
                      </span>
                    </div>
                    <button
                      onClick={() => removeFile(index)}
                      disabled={uploadProgress > 0}
                      className={`text-red-500 hover:text-red-700 transition-colors ${
                        uploadProgress > 0 ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      <Icon name="X" size={16} />
                    </button>
                  </div>
                  {/* Preflight Results Summary */}
                  {preflight && (
                    <div className="mb-2 p-2 bg-muted/50 rounded text-xs">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <span className="font-medium">Encoding:</span> {preflight?.encoding}
                        </div>
                        <div>
                          <span className="font-medium">Delimiter:</span> {preflight?.delimiter}
                        </div>
                        <div>
                          <span className="font-medium">Columns:</span> {preflight?.columnCount}
                        </div>
                        <div>
                          <span className="font-medium">Status:</span>
                          <span className={`ml-1 ${
                            preflight?.status === 'passed' ? 'text-green-600' :
                            preflight?.status === 'warning' ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {preflight?.status}
                          </span>
                        </div>
                      </div>
                      {preflight?.errors?.length > 0 && (
                        <div className="mt-1 text-xs">
                          <span className="font-medium text-red-600">Issues:</span>
                          <div className="ml-2">
                            {preflight?.errors?.map((error, idx) => (
                              <div key={idx} className="text-red-600">• {error?.message}</div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {/* Individual File Progress Bar */}
                  {(status?.status === 'processing' || status?.status === 'checking') && (
                    <div className="mb-2">
                      <div className="w-full bg-muted rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-primary to-primary/80 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${status?.progress || 0}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                  {/* File Status Message */}
                  <div className="flex items-center justify-between">
                    <span className={`text-xs ${
                      status?.status === 'completed' ? 'text-green-600' :
                      status?.status === 'error' ? 'text-red-600' :
                      status?.status === 'warning' ? 'text-yellow-600' :
                      status?.status === 'processing'? 'text-primary' : 
                      status?.status === 'checking' ? 'text-blue-600' : 'text-muted-foreground'
                    }`}>
                      {status?.message || 'Ready for preflight check'}
                    </span>
                    {(status?.status === 'processing' || status?.status === 'checking') && (
                      <span className="text-xs text-primary font-medium">
                        {status?.progress || 0}%
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Overall Progress Section */}
          {uploadProgress > 0 && (
            <div className="mt-4 p-3 bg-primary/5 border border-primary/20 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-foreground">Overall Progress</span>
                <span className="text-sm font-bold text-primary">{uploadProgress}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-3 mb-2">
                <div 
                  className="bg-gradient-to-r from-primary to-primary/80 h-3 rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                  style={{ width: `${uploadProgress}%` }}
                >
                  {uploadProgress > 10 && (
                    <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                  )}
                </div>
              </div>
              {overallStatus && (
                <p className="text-sm text-primary font-medium">{overallStatus}</p>
              )}
            </div>
          )}
          
          <div className="mt-3 flex space-x-2">
            <Button
              onClick={startUpload}
              disabled={isUploading || uploadProgress > 0 || !user || Object.values(fileStatuses)?.some(s => s?.status === 'error')}
              className={`flex items-center space-x-2 transition-all duration-300 ${
                uploadProgress > 0 ? 'bg-green-500 hover:bg-green-600' : ''
              }`}
            >
              {uploadProgress > 0 ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>{isDryRun ? 'Running Dry Run...' : 'Uploading...'}</span>
                </>
              ) : (
                <>
                  <Icon name={isDryRun ? "TestTube" : "Upload"} size={16} />
                  <span>{isDryRun ? 'Start Dry Run' : 'Start Upload'}</span>
                </>
              )}
            </Button>
            <Button
              onClick={() => {
                setSelectedFiles([]);
                setFileStatuses({});
                setPreflightResults({});
                setUploadStatus('');
              }}
              variant="outline"
              disabled={isUploading || uploadProgress > 0}
            >
              Clear All
            </Button>
          </div>
        </div>
      )}
      {/* Enhanced Upload Area with Loading Animation */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-300 ${
          dragActive
            ? 'border-primary bg-primary/10 scale-[1.02]' : 'border-muted-foreground/30 hover:border-primary/50'
        } ${isUploading || (!user && !loading) ? 'pointer-events-none opacity-60' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          multiple
          onChange={handleFileChange}
          className="hidden"
        />

        {isUploading || uploadProgress > 0 ? (
          <div className="space-y-4">
            {/* Animated Upload Icon */}
            <div className="relative w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto">
              <Icon name="Upload" size={24} color="white" />
              <div className="absolute inset-0 border-4 border-primary/30 rounded-full animate-pulse"></div>
              <div className="absolute inset-0 border-4 border-t-primary border-transparent rounded-full animate-spin"></div>
            </div>
            
            <div className="space-y-2">
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className="bg-gradient-to-r from-primary to-primary/80 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-foreground font-medium">{overallStatus || uploadStatus}</p>
                <p className="text-xs text-muted-foreground">
                  Processing files... Please wait
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className={`w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto transition-all duration-300 ${
              dragActive ? 'bg-primary/20 scale-110' : ''
            }`}>
              <Icon name="FileText" size={24} className={`transition-colors duration-300 ${
                dragActive ? 'text-primary' : 'text-muted-foreground'
              }`} />
            </div>
            
            <div>
              <p className="text-lg font-medium text-foreground mb-2">
                Drop multiple files here, or 
                <button
                  onClick={openFileDialog}
                  disabled={!user && !loading}
                  className={`text-primary hover:underline ml-1 transition-colors duration-300 ${
                    (!user && !loading) ? 'opacity-50 cursor-not-allowed' : 'hover:text-primary/80'
                  }`}
                >
                  browse
                </button>
              </p>
              <p className="text-sm text-muted-foreground">
                Supports CSV and Excel files (max 10MB each)
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Hold Ctrl/Cmd to select multiple files
              </p>
              {(!user && !loading) && (
                <p className="text-xs text-red-600 mt-2">Sign in required to upload files</p>
              )}
            </div>

            <Button
              onClick={openFileDialog}
              variant="outline"
              disabled={!user && !loading}
              className={`flex items-center space-x-2 transition-all duration-300 ${
                dragActive ? 'border-primary text-primary' : ''
              }`}
            >
              <Icon name="FolderOpen" size={16} />
              <span>Choose Multiple Files</span>
            </Button>
          </div>
        )}
      </div>
      {/* Error Message */}
      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2 animate-in fade-in duration-300">
          <Icon name="AlertCircle" size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-800">Upload Error</p>
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}
      {/* Enhanced Success Message with Results Summary */}
      {uploadStatus && !error && uploadProgress === 100 && (
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg animate-in fade-in duration-500">
          <div className="flex items-start space-x-2">
            <Icon name="CheckCircle" size={20} className="text-green-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-green-800 mb-1">Upload Successful!</p>
              <p className="text-sm text-green-700 mb-2">{uploadStatus}</p>
              
              {/* File Results Summary */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                <div className="bg-white/50 p-2 rounded border">
                  <span className="font-medium text-green-800">Completed:</span>
                  <span className="ml-1 text-green-700">
                    {Object.values(fileStatuses)?.filter(s => s?.status === 'completed')?.length} files
                  </span>
                </div>
                <div className="bg-white/50 p-2 rounded border">
                  <span className="font-medium text-green-800">Total Records:</span>
                  <span className="ml-1 text-green-700">
                    {Object.values(fileStatuses)?.reduce((total, status) => {
                      const match = status?.message?.match(/(\d+) records/);
                      return total + (match ? parseInt(match?.[1]) : 0);
                    }, 0)}
                  </span>
                </div>
                <div className="bg-white/50 p-2 rounded border">
                  <span className="font-medium text-green-800">Time:</span>
                  <span className="ml-1 text-green-700">Just now</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Enhanced File Requirements with Diagnostic Features */}
      <div className="mt-6 p-4 bg-muted/30 rounded-lg">
        <h4 className="text-sm font-medium text-foreground mb-2">Enhanced Upload Features:</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-muted-foreground">
          <div>
            <p className="font-medium text-foreground mb-1">File Support:</p>
            <ul className="space-y-1">
              <li>• Supported formats: CSV (.csv), Excel (.xlsx, .xls)</li>
              <li>• Maximum file size: 10MB per file</li>
              <li>• Automatic encoding detection (UTF-8, cp1252)</li>
              <li>• Smart delimiter detection (comma, semicolon)</li>
              <li>• Header alias mapping support</li>
            </ul>
          </div>
          <div>
            <p className="font-medium text-foreground mb-1">Diagnostics & Validation:</p>
            <ul className="space-y-1">
              <li>• Preflight validation before upload</li>
              <li>• Real-time error reporting with line numbers</li>
              <li>• Comprehensive duplicate detection analysis</li>
              <li>• Row hash collision detection</li>
              <li>• Dry run mode for testing imports</li>
            </ul>
          </div>
        </div>
        <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
          <p className="text-blue-800">
            <Icon name="Info" size={12} className="inline mr-1" />
            <strong>New:</strong> All uploads now show detailed diagnostics including rows_in_file, 
            rows_inserted, rows_skipped_duplicates, and rows_errors with first 10 error messages 
            for zero-record uploads.
          </p>
        </div>
      </div>
    </div>
  );
};

export default FileUploadZone;